import {ssrSafeDocument} from '@github-ui/ssr-utils'
import {isStaff} from '@github-ui/stats'

// Meta tag written by the dotcom server (see `BundleHelper#react_import_map_tag`). Its content
// mirrors the per-request decision the server used when choosing whether to swap in the profiling
// react-dom build ("1" = profiling build loaded, "0" = base build). Reading it here keeps this
// collector in agreement with whichever build actually loaded on the page.
const REACT_PROFILING_META = 'react-profiling'

// Enablement rate for non-staff users, used only in runtimes with no server decision.
//
// This deliberately mirrors dotcom's `BundleHelper::REACT_PROFILING_SAMPLE_BUCKET_CEILING` /
// `REACT_PROFILING_SAMPLE_BUCKETS` (200 / 10_000), but the two cannot share a literal across
// repos. Drift is tolerable rather than silent: these are disjoint audiences (a page either has
// the server meta tag or it does not, never both), and every metric is tagged with
// `profilingSource` so a rate change on one side shows up as a volume change in exactly one
// bucket. If you change this, change the dotcom constant too.
const PRODUCTION_ENABLEMENT_RATE = 0.02 // 2%

// Cached decision for the session (undefined = not yet decided)
let cachedEnabled: boolean | undefined
let cachedSource: ProfilingDecisionSource | undefined

/**
 * Where the enablement decision came from.
 *
 * - `server`: the `react-profiling` meta tag was present, so the server also chose the react-dom
 *   build and the import map matches this decision. Timings come from the real profiling build.
 * - `client-fallback`: no meta tag, so this is a locally guessed audience. There is no import map
 *   and therefore NO profiling build — `<Profiler>` still fires, but the timings come from the
 *   standard build and are not comparable to server-confirmed samples.
 */
export type ProfilingDecisionSource = 'server' | 'client-fallback'

/**
 * Reads the server's per-request profiling decision from the `react-profiling` meta tag.
 * Returns `undefined` when no server decision is present (e.g. the standalone UI service),
 * so callers can fall back to the legacy client-side audience.
 */
function readServerDecision(): boolean | undefined {
  const content = ssrSafeDocument?.head?.querySelector<HTMLMetaElement>(`meta[name="${REACT_PROFILING_META}"]`)?.content
  if (content === '1') {
    return true
  }
  if (content === '0') {
    return false
  }
  return undefined
}

interface ReactProfilingModeMethods {
  isEnabled: () => boolean
  getDecisionSource: () => ProfilingDecisionSource
  resetCache: () => void
}

/**
 * Reports whether React profiling should run for this session.
 *
 * Enablement rules:
 * - Prefer the server's per-request decision from the `react-profiling` meta tag (written by dotcom
 *   alongside the react-dom build swap), so the collector matches the build that loaded.
 * - When no server decision exists (e.g. the standalone UI service), fall back to the legacy client
 *   audience: staff enabled by default, other users sampled at 2%.
 *
 * - `isEnabled()`: Returns true if profiling is enabled for this session.
 * - `resetCache()`: Resets the cached decision (for testing only).
 */
const ReactProfilingMode: ReactProfilingModeMethods = {
  isEnabled: () => {
    // No-op on server - profiling only makes sense in the browser
    if (typeof window === 'undefined') {
      return false
    }

    // Return cached value if already decided (session-stable)
    if (cachedEnabled !== undefined) {
      return cachedEnabled
    }

    // Prefer the server's per-request decision. On dotcom the server chooses the profiling audience
    // (staff plus a small production sample), swaps the react-dom build via an import map, and
    // records the choice in the `react-profiling` meta tag — so honoring it here keeps the collector
    // aligned with the build that loaded.
    const serverDecision = readServerDecision()
    if (serverDecision !== undefined) {
      cachedEnabled = serverDecision
      cachedSource = 'server'
      return cachedEnabled
    }

    // Fallback for runtimes with no server decision (e.g. the standalone UI service): reproduce the
    // legacy client-side audience — staff by default, other users sampled at 2%.
    cachedSource = 'client-fallback'
    if (isStaff()) {
      cachedEnabled = true
      return cachedEnabled
    }

    cachedEnabled = Math.random() < PRODUCTION_ENABLEMENT_RATE
    return cachedEnabled
  },
  /**
   * Reports whether the current decision is backed by the server (and therefore by the react-dom
   * build the import map actually loaded) or is a local guess. Callers that emit telemetry should
   * tag it with this, because `client-fallback` samples are measured on the STANDARD react-dom build
   * and mixing them into profiling-build dashboards silently skews the numbers.
   *
   * Calls `isEnabled()` so the source is always populated, and is cheap once the decision is cached.
   */
  getDecisionSource: () => {
    ReactProfilingMode.isEnabled()
    return cachedSource ?? 'client-fallback'
  },
  resetCache: () => {
    cachedEnabled = undefined
    cachedSource = undefined
  },
}

export default ReactProfilingMode
